import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@chakra-ui/react';
import { Upload } from 'lucide-react';
import { importRevitShortcuts } from '../../utils/revitShortcuts';
import { notify } from '../../utils/notify';

/**
 * "Import Revit Shortcuts..." button for the Keyboard Shortcuts editor
 * footer. Reads an Autodesk Revit KeyboardShortcuts.xml export, resolves
 * each Revit PostableCommand to the equivalent app command and applies the
 * shortcut strings as user shortcuts (persisted by the command store).
 */
export function RevitShortcutImportButton() {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleImportFile = async (file: File) => {
        try {
            const summary = importRevitShortcuts(await file.text());
            if (summary.total === 0) {
                notify('No shortcuts found in the Revit shortcuts file.', 'warning');
                return;
            }
            const parts = [`${summary.applied.length} shortcut(s) applied`];
            if (summary.skipped.length > 0) parts.push(`${summary.skipped.length} skipped`);
            notify(
                `Revit shortcuts imported: ${parts.join(', ')}.`,
                summary.applied.length > 0 ? 'success' : 'warning'
            );
            // Per-entry details live in the console: the toast only carries totals.
            const unmapped = summary.skipped.filter((s) => s.reason === 'unmapped-command');
            if (unmapped.length > 0) {
                console.warn(
                    '[RevitShortcuts] Revit commands without an app equivalent:',
                    unmapped.map((s) => `${s.shortcut} → ${s.revitCommandName}`)
                );
            }
            for (const collision of summary.collisions) {
                console.warn(
                    `[RevitShortcuts] Shortcut '${collision.shortcut}' also remains assigned to '${collision.commandLabel}'.`
                );
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error.';
            notify(`Revit shortcut import failed: ${message}`, 'error');
        }
    };

    return (
        <>
            <Button
                size="sm"
                variant="outline"
                leftIcon={<Upload size={14} />}
                flexShrink={0}
                whiteSpace="nowrap"
                onClick={() => fileInputRef.current?.click()}
            >
                {t('dialogs.ui.importRevitShortcuts')}
            </Button>
            <input
                ref={fileInputRef}
                type="file"
                accept=".xml,.txt,application/xml,text/xml"
                className="hidden-file-input"
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleImportFile(file);
                    e.target.value = '';
                }}
            />
        </>
    );
}
